"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Globe as Globe2, UserCheck, Clock, TrendingUp, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const coveragePoints = [{
  title: "UK & Global Search Strategy",
  desc: "Targeted campaigns built for competitive domestic rankings across the UK alongside multiregional and international search expansion."
}, {
  title: "Hands-on Technical Execution",
  desc: "Every technical audit, site architecture restructure, and schema deployment is personally implemented with end-to-end quality control."
}];
const directAdvantages = [{
  title: "Lean Consultancy Model",
  desc: "A streamlined consultancy structure keeps the focus on strategy, implementation and measurable SEO priorities."
}, {
  title: "Flexible Engagement Terms",
  desc: "Choose from clearly scoped consulting, project or ongoing engagement options based on the level of SEO support your business requires."
}, {
  title: "Maximum Return on Spend",
  desc: "Targeted, lean implementation tailored specifically to your commercial goals for verifiable ranking and revenue growth."
}];
const comparisonFreelancePoints = ["Direct strategy & execution by Yassine", "Tailored roadmap matched to your revenue goals", "Flexible hourly or retained engagements", "Full ownership of all audits, documents & assets"];
const comparisonAgencyPoints = ["Multi-person delivery structure", "Account-management structure may vary by provider", "Monthly or project-based packages", "Contract terms vary by agency"];
export default function CostTransparency() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="costtransparencyandtrust" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide uppercase">
            <Sparkles className="size-3.5 text-accent" />
            Clarity & Accountability
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-default font-bold text-foreground tracking-tight leading-tight">
            Transparent SEO Pricing & Authentic Consultant Trust
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            I believe search engine optimisation should be transparent. When you hire YE Consultancy, you invest in experienced SEO consultancy with clear costs, defined deliverables and a strategy aligned with your business objectives.
          </p>
        </div>

        {/* 4 Block Layout Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="space-y-12">
          {/* Top Two Column Cards: UK & International Coverage & Direct Model */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Block 1: UK-Based SEO Consultant, Working Internationally */}
            <motion.div variants={itemVariants}>
              <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-300">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Globe2 className="size-5" />
                    </div>
                    <Badge variant="outline" className="text-xs bg-secondary/50 border-secondary">
                      UK & Worldwide
                    </Badge>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
                    UK-Based SEO Consultant, Working Internationally
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm">
                    UK-headquartered independent search consultancy partnering remotely with organisations across domestic and international markets.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-2">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Operating as an established <Link to="/SeoConsultantUkPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">SEO Consultant UK</Link>, I partner remotely with ambitious businesses located across major <Link to="/UkCitiesAreasPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">UK Cities & Areas</Link> as well as clients throughout Europe, the United States, Canada, Australia, New Zealand and other key <Link to="/InternationalLocationsPage" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">International Locations</Link>. Every search engagement is structured around regional market dynamics, multilingual or cross-border considerations, and strict compliance standards.
                  </p>
                  <Separator />
                  <div className="space-y-3 pt-2">
                    {coveragePoints.map((point, index) => <div key={point.title} data-index={index} className="flex items-start gap-3">
                        <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-foreground">{point.title}</p>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{point.desc}</p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Block 2: Flexible SEO Consultancy */}
            <motion.div variants={itemVariants}>
              <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-300">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <UserCheck className="size-5" />
                    </div>
                    <Badge variant="outline" className="text-xs bg-secondary/50 border-secondary">
                      Commercial Value
                    </Badge>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
                    Flexible SEO Consultancy
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm">
                    Focused SEO strategy with transparent pricing and flexible engagement options.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-2">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    An independent consultancy model provides a lean and flexible way to access experienced SEO support. Engagements can be structured around your priorities, available resources and commercial objectives.
                  </p>
                  <Separator />
                  <div className="space-y-3 pt-2">
                    {directAdvantages.map((adv, index) => <div key={adv.title} data-index={index} className="flex items-start gap-3">
                        <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-foreground">{adv.title}</p>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{adv.desc}</p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Block 3: Cost Transparency Comparison */}
          <motion.div variants={itemVariants}>
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
              <div className="max-w-2xl mb-8">
                <Badge variant="outline" className="mb-2 text-xs uppercase tracking-wider font-semibold">
                  Cost Breakdown
                </Badge>
                <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
                  Clear, Honest SEO Investment Rates
                </h3>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                  On SEO consultant cost, freelance SEO consultants often charge £50–£100 per hour, while SEO agencies typically start around £1,500 per month. Cheap SEO often cuts corners and can undermine long-term results.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
                {/* Independent Consultant Rate Card */}
                <div className="rounded-xl border-2 border-primary bg-background p-6 flex flex-col justify-between relative shadow-sm">
                  <div className="absolute -top-3 right-6 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
                    Direct Expert Value
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="size-4 text-primary" />
                      <span className="text-xs font-semibold text-primary uppercase tracking-wide">YE Consultancy Rate</span>
                    </div>
                    <p className="text-xl font-bold text-foreground">Freelance SEO Consultant</p>
                    <div className="mt-4 mb-6 flex items-baseline gap-1">
                      <span className="text-3xl sm:text-4xl font-extrabold text-foreground">£50 – £100</span>
                      <span className="text-sm text-muted-foreground">/ hour</span>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                      Tailored project and monthly retainer options calculated purely on active strategic hours with no hidden fees.
                    </p>
                    <Separator className="mb-6" />
                    <ul className="space-y-3">
                      {comparisonFreelancePoints.map((point, index) => <li key={point} data-index={index} className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>

                {/* SEO Agency Rate Card */}
                <div className="rounded-xl border border-border bg-muted/40 p-6 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="size-4 text-muted-foreground" />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Traditional Agency</span>
                    </div>
                    <p className="text-xl font-bold text-foreground">SEO Agency Retainer</p>
                    <div className="mt-4 mb-6 flex items-baseline gap-1">
                      <span className="text-3xl sm:text-4xl font-extrabold text-foreground">£1,500+</span>
                      <span className="text-sm text-muted-foreground">/ month</span>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                      Standard monthly packages reflecting larger multi-disciplinary team structures and broader organizational overhead costs.
                    </p>
                    <Separator className="mb-6" />
                    <ul className="space-y-3">
                      {comparisonAgencyPoints.map((point, index) => <li key={point} data-index={index} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                          <span className="size-1.5 rounded-full bg-muted-foreground shrink-0 mt-2" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Block 4: Trust & Case Study CTA Card */}
          <motion.div variants={itemVariants}>
            <div className="rounded-2xl border border-border bg-gradient-to-r from-card via-card to-secondary/30 p-8 sm:p-10 lg:p-12 shadow-xs">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                <div className="max-w-2xl space-y-3">
                  <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                    <ShieldCheck className="size-4 text-primary" />
                    Verified Client Outcomes
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
                    Real Case Studies, Verified Growth & No Vanity Metrics
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    If you need a trusted SEO consultant, use the case studies below to judge whether Ye-Consultancy is the right SEO expert for your goals, especially if you want a more direct, measurable and hands-on approach than other SEO consultants typically offer. If you are comparing options for the best SEO consultant, the right fit should come down to relevant results, clear strategy, a proven track record and case studies that show measurable outcomes.
                  </p>
                </div>
                <div className="shrink-0 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 shadow-sm">
                    <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
                      Explore My Case Studies
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent/10">
                    <Link to="/ContactPage">
                      Hire Me Directly
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}